import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface AsyncErrorStateProps {
  /** Stack layout: defaults to "Something went wrong" when omitted. Inline: omit for single-line destructive message. */
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Optional second action (e.g. navigate away). */
  secondaryAction?: { label: string; onClick: () => void };
  /** `stack` = centered (lists, cards). `inline` = horizontal strip (admin banners). */
  layout?: 'stack' | 'inline';
  compact?: boolean;
  className?: string;
  disabled?: boolean;
  /** Stack layout only: show refresh icon on retry button */
  showRetryIcon?: boolean;
}

/**
 * Shared async / fetch failure UI. Wording aligned with LazyLoad / ErrorBoundary retry patterns.
 */
export function AsyncErrorState({
  title,
  message,
  onRetry,
  retryLabel = 'Try Again',
  secondaryAction,
  layout = 'stack',
  compact = false,
  className,
  disabled = false,
  showRetryIcon = true,
}: AsyncErrorStateProps) {
  if (layout === 'inline') {
    return (
      <div
        role="alert"
        className={cn(
          'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
          className,
        )}
      >
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
            aria-hidden
          />
          <div className="min-w-0">
            {title ? (
              <>
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="text-sm text-muted-foreground break-words">{message}</p>
              </>
            ) : (
              <p className="text-sm text-destructive break-words">{message}</p>
            )}
          </div>
        </div>
        {(onRetry || secondaryAction) && (
          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
            {onRetry ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onRetry}
                disabled={disabled}
                className="w-full sm:w-auto"
              >
                {retryLabel}
              </Button>
            ) : null}
            {secondaryAction ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={secondaryAction.onClick}
                className="w-full sm:w-auto"
              >
                {secondaryAction.label}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  const heading = title ?? 'Something went wrong';

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'py-4 px-2' : 'py-8 px-4',
        className,
      )}
    >
      <AlertCircle
        className={cn('mb-2 text-destructive', compact ? 'h-6 w-6' : 'h-8 w-8')}
        aria-hidden
      />
      <p className="mb-1 text-sm font-semibold text-foreground">{heading}</p>
      <p className="mb-4 max-w-md text-sm text-muted-foreground break-words">
        {message}
      </p>
      {(onRetry || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {onRetry ? (
            <Button
              variant="outline"
              size={compact ? 'sm' : 'default'}
              onClick={onRetry}
              disabled={disabled}
            >
              {showRetryIcon ? (
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
              ) : null}
              {retryLabel}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button
              type="button"
              variant="secondary"
              size={compact ? 'sm' : 'default'}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
